import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { User, Lock, Save, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Le nom ne peut pas être vide");
    setSavingName(true);
    try {
      const res = await api.put("/auth/profile", { name: name.trim() });
      if (setUser) setUser(prev => ({ ...prev, name: res.data.name }));
      toast.success("Nom mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setSavingName(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) return toast.error("Remplissez tous les champs");
    if (newPassword.length < 6) return toast.error("Le mot de passe doit contenir au moins 6 caractères");
    if (newPassword !== confirmPassword) return toast.error("Les mots de passe ne correspondent pas");
    setSavingPwd(true);
    try {
      await api.put("/auth/password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Mot de passe modifié avec succès");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du changement de mot de passe");
    }
    setSavingPwd(false);
  };

  return (
    <div data-testid="profile-page" className="max-w-2xl">
      <div className="mb-8">
        <h1 className="font-heading text-3xl">Mon profil</h1>
        <p className="text-muted-foreground mt-1">Gérez vos informations personnelles et votre mot de passe</p>
      </div>

      {/* Profile Info */}
      <Card className="border rounded-none mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <User className="h-4 w-4 text-primary" /> Informations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateName} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input value={user?.email || ""} disabled className="rounded-none mt-1.5 bg-muted/50" data-testid="profile-email" />
              <p className="text-xs text-muted-foreground mt-1">L'email ne peut pas être modifié</p>
            </div>
            <div>
              <Label>Role</Label>
              <Input value={user?.role === "admin" ? "Administrateur" : "Éditeur"} disabled className="rounded-none mt-1.5 bg-muted/50" data-testid="profile-role" />
            </div>
            <div>
              <Label>Nom</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-none mt-1.5"
                placeholder="Votre nom"
                data-testid="profile-name-input"
              />
            </div>
            <Button type="submit" className="rounded-none" disabled={savingName} data-testid="save-name-btn">
              {savingName ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border rounded-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" /> Changer le mot de passe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Mot de passe actuel</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="rounded-none mt-1.5"
                placeholder="Votre mot de passe actuel"
                data-testid="current-password-input"
              />
            </div>
            <Separator />
            <div>
              <Label>Nouveau mot de passe</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="rounded-none mt-1.5"
                placeholder="Minimum 6 caractères"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="rounded-none mt-1.5"
                placeholder="Retapez le nouveau mot de passe"
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" className="rounded-none" disabled={savingPwd} data-testid="change-password-btn">
              {savingPwd ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
              Modifier le mot de passe
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
